/**
 * Normalize official Nexi App2App callback fields.
 * Android SoftPOS can return f39/f38/f41, whereas MobilePOS uses
 * actionCode/authorizationNumber/terminalId. This is parsing only:
 * the callback itself is NOT independent server-side proof of settlement.
 */
export type NexiApp2AppCallback = {
  callerTrxId: string
  result: number
  amount: number
  actionCode: string
  operationType: string
  terminalId: string | null
  authorizationNumber: string | null
}

export function parseNexiApp2AppCallback(params: URLSearchParams): NexiApp2AppCallback {
  const pick = (...names: string[]): string => {
    for (const name of names) {
      for (const [key, value] of params.entries()) {
        if (key.toLowerCase() === name.toLowerCase() && value.trim() !== '') return value.trim()
      }
    }
    return ''
  }
  const rawResult = pick('result', 'resultCode')
  const rawAmount = pick('amount')
  return {
    callerTrxId: pick('callerTrxId', 'caller_trx_id'),
    result: rawResult === '' ? NaN : Number(rawResult),
    amount: rawAmount === '' ? NaN : Number(rawAmount),
    actionCode: pick('actionCode', 'action_code', 'f39'),
    operationType: pick('operationType', 'operation_type').toUpperCase(),
    terminalId: pick('terminalId', 'terminal_id', 'f41') || null,
    authorizationNumber: pick('authorizationNumber', 'authorization_number', 'f38') || null,
  }
}

/**
 * Accept cancellation/error callbacks even when Nexi omits amount.
 * Approval requires a positive integer amount and PAYMENT (when supplied).
 * The database finalizer still checks result, approval code, order and amount.
 */
export function isValidNexiApp2AppCallback(response: NexiApp2AppCallback): boolean {
  if (!response.callerTrxId || !Number.isInteger(response.result)) return false
  if (response.result !== 0) return true
  return Number.isSafeInteger(response.amount) && response.amount > 0 &&
    (!response.operationType || response.operationType === 'PAYMENT')
}
